const jsonUnmarshallMetadataFields = ['sf_detectInputContexts', 'sf_key', 'sf_memberOf'];
const jsonUnmarshallPropertiesFields = ['inputSources', 'inputValues', 'inputs', 'sources'];

/*
 * Manual unmarshalling of JSON-encoded fields will soon no longer be
 * necessary as this step will be taken care of by SignalBoost itself.
 */
export function unmarshalJsonFields(obj, fields) {
    fields.forEach(function (name) {
        if (obj[name] && typeof obj[name] === 'string') {
            try {
                obj[name] = JSON.parse(obj[name]);
            } catch (e) {
                $log.warn('Unable to unmarshall JSON field ' + name + ' from ' + obj, e);
            }
        }
    });
}

/**
 * Coerce the given event properties' schema into the latest alert event schema.
 *
 * Handles all prior schema versions and modifies the event properties to
 * match the latest alert event schema. As of October 2016, that's schema
 * v3, which is based around the following structure for the 'inputs'
 * property:
 *
 * "inputs": {
 *   "_S0": {
 *     "key": {
 *       "dim1": "dim1value",
 *       ...
 *     },
 *     "value": 42
 *   }
 * }
 */
export function coerceAlertEventSchema(properties) {
    properties.inputs = {};

    if (properties.inputSources) {
        // v2 schema events should have an inputSources
        Object.entries(properties.inputSources || {}).forEach(([name, dimensions]) => {
            let value;
            switch (name) {
                case 'high':
                    value = properties.highThreshold;
                    break;
                case 'low':
                    value = properties.lowThreshold;
                    break;
                default:
                    if (!properties.inputValues) {
                        return;
                    }

                    value = properties.inputValues[name];
                    break;
            }

            properties.inputs[name] = {
                key: dimensions,
                value: value,
            };
        });
    } else if (properties.inputValues) {
        // Some early v2 detector events might have been created without an
        // sf_schema and without inputSources.
        Object.entries(properties.inputValues || {}).forEach(([name, value]) => {
            properties.inputs[name] = {
                key: {},
                value: value,
            };
        });
    }

    if (properties.value) {
        if (typeof properties.sources === 'string') {
            // In very old events, sources can be a comma-separated of dimension
            // values.
            const sources = properties.sources.split(',');
            properties.sources = {};
            sources.forEach(function (item, index) {
                sources[index] = item;
            });
        } else if (typeof properties.sources === 'undefined') {
            // It's also possible for sources to not be present at all on v1
            // detector events that detected on the default group.
            properties.sources = {};
        }

        properties.inputs.in = {
            key: properties.sources,
            value: properties.value,
        };
    }

    /*
     * For v1 schema, or if high and low thresholds weren't in the
     * inputSources because they were static thresholds, capture them now
     * (without a `key`).
     */
    if (properties.highThreshold && !properties.inputs.high) {
        properties.inputs.high = {
            value: properties.highThreshold,
        };
    }
    if (properties.lowThreshold && !properties.inputs.low) {
        properties.inputs.low = {
            value: properties.lowThreshold,
        };
    }

    delete properties.inputSources;
    delete properties.inputValues;
    delete properties.sources;
    delete properties.highThreshold;
    delete properties.lowThreshold;
}

export function coerceAlertEventSchemaIfNecessary(item) {
    if (item.metadata.sf_eventCategory === 'ALERT' && item.properties.sf_schema !== 3) {
        coerceAlertEventSchema(item.properties);
    }
}

export function prepareEvent(event) {
    // Unmarshall fields that are JSON-encoded
    unmarshalJsonFields(event.metadata, jsonUnmarshallMetadataFields);
    unmarshalJsonFields(event.properties, jsonUnmarshallPropertiesFields);

    // Schema coercion only matters for ALERT events for schema versions different than 3.
    coerceAlertEventSchemaIfNecessary(event);

    // Gather detect contexts for available inputs.
    const contexts = event.metadata.sf_detectInputContexts;

    Object.entries(event.properties.inputs || {}).forEach(([name, input]) => {
        input.identifier = name;
        if (contexts && contexts[name]) {
            Object.assign(input, contexts[name]);
        }
    });

    return event;
}
